// src/pages/ForumMyPage.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  subscribeToInboxMergedData,
} from "../services/messagesDataService";

import {
  getMyForumPostsData,
  updateForumPostData,
  deleteForumPostData,
  uploadForumImagesData,
  subscribeToForumBadgeData,
} from "../services/forumDataService";

import { getUserProfileData } from "../services/profileDataService";

import "./ForumMyPage.css";
import GateIcon from "../components/GateIcon";
import OfflineBanner from "../components/OfflineBanner";
import LoginHintModal from "../components/LoginHintModal";
import SpeechIcon from "../components/SpeechIcon";
import { useBlockedUsers } from "../hooks/useBlockedUsers";

import { useAuth } from "../auth/AuthProvider";


const MAX_IMAGES = 5;
const SEEN_REPLIES_KEY = "forum_seen_replies_v1";

const readSeenRepliesMap = () => {
  try {
    return JSON.parse(localStorage.getItem(SEEN_REPLIES_KEY) || "{}");
  } catch {
    return {};
  }
};

const writeSeenRepliesMap = (map) => {
  try {
    localStorage.setItem(SEEN_REPLIES_KEY, JSON.stringify(map));
  } catch {}
};

function ForumMyPage() {
  const navigate = useNavigate();

  const { user: authUser, authLoading } = useAuth();
  const myUid = authUser?.uid || null;
  const isVerified = authUser?.emailVerified === true;

const {
  isBlocked,
  blockedUserIds,
} = useBlockedUsers(myUid, isVerified);

  const [unreadCount, setUnreadCount] = useState(0);

  const [seenRepliesMap, setSeenRepliesMap] = useState(() => readSeenRepliesMap());

  // zusätzliche Badge-Daten für Abos im Dropdown
  const [subscribedPostsForBadge, setSubscribedPostsForBadge] = useState([]);

  // Reply-Aktivität
  const [myReplyActivityMap, setMyReplyActivityMap] = useState({});
  const [abosReplyActivityMap, setAbosReplyActivityMap] = useState({});

  useEffect(() => {
  let unsub = null;
  let mounted = true;

  const initUnread = async () => {
    if (!myUid) {
      setUnreadCount(0);
      return;
    }

    try {
      unsub = subscribeToInboxMergedData(myUid, (merged) => {
        if (!mounted) return;

        const list = Array.isArray(merged) ? merged : [];
       const c = list.reduce((acc, m) => {
  const senderId = m?.senderId || "";
  const isUnread = m?.recipientId === myUid && m?.read === false;

  if (!isUnread) return acc;

  if (senderId && isBlocked(senderId)) return acc;

  return acc + 1;
}, 0);

        setUnreadCount(c);
      });
    } catch {
      if (mounted) setUnreadCount(0);
    }
  };

  initUnread();

  return () => {
    mounted = false;
    if (typeof unsub === "function") unsub();
  };
}, [myUid, isBlocked, blockedUserIds]);

  const [isOffline, setIsOffline] = useState(false);
  const [retryAttempt, setRetryAttempt] = useState(0);

  const [showLoginHint, setShowLoginHint] = useState(false);

  const [posts, setPosts] = useState([]);
  const [postsError, setPostsError] = useState("");

  const [postToDelete, setPostToDelete] = useState(null);

  const [postToEdit, setPostToEdit] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editContent, setEditContent] = useState("");

  const [existingImageUrls, setExistingImageUrls] = useState([]);
  const [newImageFiles, setNewImageFiles] = useState([]);
  const [newImagePreviews, setNewImagePreviews] = useState([]);
  const [mainImageIndex, setMainImageIndex] = useState(null);
  const [showImageLimitPopup, setShowImageLimitPopup] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 20;
  const totalPages = Math.max(1, Math.ceil(posts.length / postsPerPage) || 1);

  const currentPosts = useMemo(() => {
    return posts.slice((currentPage - 1) * postsPerPage, currentPage * postsPerPage);
  }, [posts, currentPage]);

  const fileInputRef = useRef(null);

  useEffect(() => {
    const checkConnection = async () => {
      try {
        await fetch("https://www.gstatic.com/firebasejs/9.6.1/firebase-app.js", {
          method: "HEAD",
          cache: "no-store",
        });
        setIsOffline(false);
      } catch (err) {
        console.warn("Keine Server-Verbindung möglich (ForumMyPage):", err);
        setIsOffline(true);
      }
    };
    checkConnection();
  }, [retryAttempt]);

  const handleOfflineRetry = () => {
    setIsOffline(false);
    setTimeout(() => setRetryAttempt((prev) => prev + 1), 600);
  };

  useEffect(() => {
    if (authLoading) return;
    if (!myUid) setShowLoginHint(true);
  }, [authLoading, myUid]);

  const toMillis = (ts, fallback) => {
  if (ts && typeof ts.toMillis === "function") return ts.toMillis();
  if (typeof fallback === "string") return Date.parse(fallback) || 0;
  return 0;
};
const toDisplayMillis = (value) => {
  if (!value) return 0;

  if (typeof value === "number") return value;

  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  if (typeof value.toMillis === "function") {
    return value.toMillis();
  }

  if (typeof value.seconds === "number") {
    return value.seconds * 1000;
  }

  return 0;
};

const formatCreatedDate = (item) => {
  const millis =
    toDisplayMillis(item?.createdAt) ||
    toDisplayMillis(item?.createdAtClient) ||
    toDisplayMillis(item?.createdAtMs) ||
    toDisplayMillis(item?.updatedAt) ||
    toDisplayMillis(item?.updatedAtClient);

  if (!millis) return "";

  return new Date(millis).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

 // Meine Beiträge laden
useEffect(() => {
  let mounted = true;

  const loadMyPosts = async () => {
    if (authLoading) return;

    if (!myUid) {
      setPosts([]);
      return;
    }

    setPostsError("");

    try {
      const list = await getMyForumPostsData(myUid);

      if (!mounted) return;

     const sorted = Array.isArray(list)
  ? [...list].sort((a, b) => {
      const at = toMillis(a.createdAt, a.createdAtClient);
      const bt = toMillis(b.createdAt, b.createdAtClient);
      return bt - at;
    })
  : [];

      setPosts(sorted);

      const newTotal = Math.max(1, Math.ceil(sorted.length / postsPerPage) || 1);
      setCurrentPage((p) => Math.min(p, newTotal));
    } catch (err) {
      console.warn("⚠️ Fehler beim Laden (ForumMyPage):", err);
      if (!mounted) return;
      setPostsError("");
      setPosts([]);
    }
  };

  loadMyPosts();

  return () => {
    mounted = false;
  };
}, [authLoading, myUid]);

useEffect(() => {
  let unsub = null;
  let mounted = true;

  const initBadges = async () => {
    if (!myUid) {
      setMyReplyActivityMap({});
      setAbosReplyActivityMap({});
      setSubscribedPostsForBadge([]);
      return;
    }

    try {
      unsub = await subscribeToForumBadgeData(myUid, ({ myReplies, abosReplies }) => {
        if (!mounted) return;
        setMyReplyActivityMap(myReplies || {});
        setAbosReplyActivityMap(abosReplies || {});
      });
    } catch (err) {
      console.warn("ForumMyPage: subscribeToForumBadgeData fehlgeschlagen:", err);
      if (mounted) {
        setMyReplyActivityMap({});
        setAbosReplyActivityMap({});
      }
    }
  };

  initBadges();

  return () => {
    mounted = false;
    if (typeof unsub === "function") unsub();
  };
}, [myUid]);

const myUnreadReplyCount = Object.values(myReplyActivityMap).reduce((acc, meta) => {
  const postId = meta?.postId;
  const latestTs = Number(meta?.latestTs || 0);
  const lastSeen = Number(seenRepliesMap[postId] || 0);
  const latestAuthorId = meta?.latestAuthorId || "";

  if (!postId) return acc;
  if (!latestTs || latestTs <= lastSeen) return acc;
  if (!latestAuthorId || latestAuthorId === myUid) return acc;

  if (isBlocked(latestAuthorId)) return acc;

  return acc + 1;
}, 0);

 const abosUnreadReplyCount = Object.values(abosReplyActivityMap).reduce((acc, meta) => {
  const postId = meta?.postId;
  const latestTs = Number(meta?.latestTs || 0);
  const lastSeen = Number(seenRepliesMap[postId] || 0);
  const latestAuthorId = meta?.latestAuthorId || "";

  if (!postId) return acc;
  if (!latestTs || latestTs <= lastSeen) return acc;
  if (!latestAuthorId || latestAuthorId === myUid) return acc;

  if (isBlocked(latestAuthorId)) return acc;

  return acc + 1;
}, 0);

const dropdownKey = `${myUid || "guest"}-${myUnreadReplyCount}-${abosUnreadReplyCount}-${unreadCount}`;

  const markPostAsSeen = (postId) => {
  const meta =
    myReplyActivityMap[postId] ||
    abosReplyActivityMap[postId] ||
    null;

  const currentSeen = Number(seenRepliesMap[postId] || 0);
  const ts = Math.max(currentSeen, Number(meta?.latestTs || 0), Date.now());

  setSeenRepliesMap((prev) => {
    const next = { ...prev, [postId]: ts };
    writeSeenRepliesMap(next);
    return next;
  });
};

  const handleOpenPost = (postId) => {
    markPostAsSeen(postId);
    navigate(`/forum/beitrag/${postId}`);
  };

  const handleDropdownChange = (e) => {
    const value = e.target.value;
    if (value === "my") return;
    navigate(value ? `/forum/${value}` : "/forum");
  };

  const confirmDelete = async () => {
  const id = postToDelete;
  if (!id) return;

  try {
    await deleteForumPostData(id);

    setPosts((prev) => prev.filter((p) => p.id !== id));
    setPostToDelete(null);
  } catch (err) {
    console.warn("⚠️ Löschen fehlgeschlagen:", err);
    alert("Beitrag konnte nicht gelöscht werden. Bitte erneut versuchen.");
    setPostToDelete(null);
  }
};

  const getAuthorNameFromProfile = async (uid) => {
  try {
    const profile = await getUserProfileData(uid);
    return String(profile?.name || "").trim();
  } catch (err) {
    console.warn("⚠️ Profilname konnte nicht geladen werden:", err);
    return "";
  }
};

  const startEdit = async (post) => {
    setPostToEdit(post.id);
    setEditTitle(post.title || "");
    setEditContent(post.content || "");

    const urls = Array.isArray(post.images) ? post.images : [];
    setExistingImageUrls(urls);

    newImagePreviews.forEach((p) => {
      try {
        URL.revokeObjectURL(p);
      } catch {}
    });
    setNewImageFiles([]);
    setNewImagePreviews([]);

    const idx =
      typeof post.mainImageIndex === "number" ? post.mainImageIndex : urls.length > 0 ? 0 : null;
    setMainImageIndex(idx);

    if (!post.authorName && myUid) {
      const profileName = await getAuthorNameFromProfile(myUid);
      if (profileName) {
        try {
          await updateForumPostData(post.id, { authorName: profileName });
        } catch {}
      }
    }
  };

  const closeEdit = () => {
    setPostToEdit(null);
    setEditTitle("");
    setEditContent("");
    setExistingImageUrls([]);

    newImagePreviews.forEach((p) => {
      try {
        URL.revokeObjectURL(p);
      } catch {}
    });
    setNewImageFiles([]);
    setNewImagePreviews([]);

    setMainImageIndex(null);
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const currentCount = existingImageUrls.length + newImageFiles.length;
    const remaining = MAX_IMAGES - currentCount;

    if (remaining <= 0) {
      setShowImageLimitPopup(true);
      e.target.value = null;
      return;
    }

    const accepted = files.slice(0, remaining);
    const previews = accepted.map((f) => URL.createObjectURL(f));

    setNewImageFiles((prev) => [...prev, ...accepted]);
    setNewImagePreviews((prev) => [...prev, ...previews]);

    setMainImageIndex((prev) => {
      const totalAfter = existingImageUrls.length + (newImageFiles.length + accepted.length);
      if (prev === null && totalAfter > 0) return 0;
      return prev;
    });

    e.target.value = null;
  };

  const removeExistingImage = (index) => {
    setExistingImageUrls((prev) => {
      const updated = prev.filter((_, i) => i !== index);

      setMainImageIndex((mi) => {
        if (mi === null) return mi;
        if (mi === index) return updated.length + newImageFiles.length > 0 ? 0 : null;
        if (mi > index) return mi - 1;
        return mi;
      });

      return updated;
    });
  };

  const removeNewImage = (index) => {
    setNewImageFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));

    setNewImagePreviews((prevPrev) => {
      const removed = prevPrev[index];
      try {
        if (removed) URL.revokeObjectURL(removed);
      } catch {}

      const updatedPrev = prevPrev.filter((_, i) => i !== index);

      setMainImageIndex((mi) => {
        if (mi === null) return mi;

        const existingCount = existingImageUrls.length;
        const newIndexGlobal = existingCount + index;

        if (mi === newIndexGlobal) {
          const totalAfter = existingCount + updatedPrev.length;
          return totalAfter > 0 ? 0 : null;
        }
        if (mi > newIndexGlobal) return mi - 1;
        return mi;
      });

      return updatedPrev;
    });
  };

  const uploadNewImagesToStorage = async (uid, files) => {
  if (!Array.isArray(files) || files.length === 0) return [];

  return await uploadForumImagesData(
    files,
    `forumPosts/${uid || "anonymous"}/posts/${postToEdit || "edit"}`
  );
};

  const confirmEdit = async () => {
    if (!postToEdit) return;

    if (!editTitle.trim() || !editContent.trim()) {
      alert("Titel und Inhalt dürfen nicht leer sein.");
      return;
    }

    if (!myUid) {
      setShowLoginHint(true);
      return;
    }

    try {
      const uploadedUrls = await uploadNewImagesToStorage(myUid, newImageFiles);
      const finalImages = [...existingImageUrls, ...uploadedUrls];

      let finalMainIndex = mainImageIndex;
      if (finalMainIndex === null) {
        finalMainIndex = finalImages.length > 0 ? 0 : null;
      } else if (finalImages.length === 0) {
        finalMainIndex = null;
      } else if (finalMainIndex >= finalImages.length) {
        finalMainIndex = 0;
      }

      const updated = {
        title: editTitle.trim(),
        content: editContent.trim(),
        images: finalImages,
        mainImageIndex: finalMainIndex,
      };

      const profileName = await getAuthorNameFromProfile(myUid);
      if (profileName) updated.authorName = profileName;

      await updateForumPostData(postToEdit, updated);

      closeEdit();
    } catch (err) {
      console.warn("⚠️ Bearbeiten fehlgeschlagen:", err);
      alert("Beitrag konnte nicht gespeichert werden. Bitte erneut versuchen.");
    }
  };

  const openFilePicker = () => {
    const currentCount = existingImageUrls.length + newImageFiles.length;
    if (currentCount >= MAX_IMAGES) {
      setShowImageLimitPopup(true);
      return;
    }
    fileInputRef.current?.click();
  };

  const authorLabel = (post) => post.authorName || post.author || "Du";
  const showEmptyState = !authLoading && myUid && currentPosts.length === 0 && postToEdit === null;

  return (
    <div className="forum-container">
      {isOffline && <OfflineBanner onRetry={handleOfflineRetry} />}

      {showLoginHint && <LoginHintModal onClose={() => setShowLoginHint(false)} />}

      <img
        src="/logo-edens-ernte-transparent.png"
        alt="Logo"
        className="browse-logo"
        onClick={() => navigate("/")}
      />
      <SpeechIcon
  className="speech-icon no-tap-highlight"
  unreadCount={unreadCount}
  myUnreadReplyCount={myUnreadReplyCount}
  abosUnreadReplyCount={abosUnreadReplyCount}
/>

<GateIcon className="login-icon" />

<img
  src="/rezept-icon.png"
  alt="Rezepthelfer Icon"
  className="rezept-icon"
  onClick={() => navigate('/rezepte')}
/>
      <p className="top-claim">Zusammen wachsen - im Forum!</p>
      <div className="page-background" />

      <div className="forum-main-box">
        <div className="forum-dropdown">
          <div className="custom-select-wrapper" key={`forum-my-dropdown-${dropdownKey}`}>
          <select
          value="my"
           onChange={handleDropdownChange}
          key={`forum-my-select-${dropdownKey}`}
          >

              <option value="">Alle Beiträge</option>
              <option value="my"> {myUnreadReplyCount > 0 ? "🔴" : ""} Meine Beiträge
            </option>
              <option value="abos">{abosUnreadReplyCount > 0 ? "🔴 " : ""}Abonnierte Beiträge</option>
              <option value="messages">{unreadCount > 0 ? "🔴" : ""} Nachrichten</option>
            </select>
            <span className="arrow-down">▼</span>
          </div>
        </div>

        {postsError && <div className="error-message forum-error">{postsError}</div>}

        {authLoading ? (
          <p style={{ textAlign: "center", opacity: 0.8 }}>Lade Benutzer…</p>
        ) : !myUid ? (
          <p>Bitte einloggen, um deine Beiträge zu sehen.</p>
        ) : showEmptyState ? (
          <p>Du hast noch keine eigenen Beiträge erstellt.</p>
        ) : (
          currentPosts.map((post) =>
            post.id === postToEdit ? (
              <div key={post.id} className="post-box compact-post">
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  placeholder="Titel"
                  className="edit-input"
                />

                <div className="upload-textarea-wrapper">
                  <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    placeholder="Inhalt"
                    className="edit-textarea"
                  />
                  <img
                    src="/upload-icon.png"
                    alt="Upload"
                    className="my-upload-icon-small"
                    onClick={openFilePicker}
                    title="Bilder hinzufügen"
                  />
                </div>

                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  ref={fileInputRef}
                  style={{ display: "none" }}
                />

                <div className="image-preview-row">
                  {existingImageUrls.map((src, i) => {
                    const isMain = mainImageIndex === i;
                    return (
                      <div key={`ex-${i}`} className={`image-item ${isMain ? "main-image" : ""}`}>
                        <img src={src} alt={`existing-${i}`} className="preview-image" />
                        <div className="image-actions">
                          <button type="button" onClick={() => removeExistingImage(i)}>
                            X
                          </button>
                          <button type="button" onClick={() => setMainImageIndex(i)}>
                            ★
                          </button>
                        </div>
                      </div>
                    );
                  })}

                  {newImagePreviews.map((src, i) => {
                    const globalIndex = existingImageUrls.length + i;
                    const isMain = mainImageIndex === globalIndex;
                    return (
                      <div key={`new-${i}`} className={`image-item ${isMain ? "main-image" : ""}`}>
                        <img src={src} alt={`new-${i}`} className="preview-image" />
                        <div className="image-actions">
                          <button type="button" onClick={() => removeNewImage(i)}>
                            X
                          </button>
                          <button type="button" onClick={() => setMainImageIndex(globalIndex)}>
                            ★
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <button className="green-button" onClick={confirmEdit}>
                  Speichern
                </button>
                <button className="red-button" onClick={closeEdit} style={{ marginTop: 10 }}>
                  Abbrechen
                </button>
              </div>
            ) : (
  <div className="message-row" key={post.id}>
    {myReplyActivityMap[post.id]?.latestTs >
  Number(seenRepliesMap[post.id] || 0) &&
  myReplyActivityMap[post.id]?.latestAuthorId &&
  myReplyActivityMap[post.id]?.latestAuthorId !== myUid &&
  !isBlocked(myReplyActivityMap[post.id]?.latestAuthorId) && (
    <span className="unread-icon">❗</span>
)}

    <div
      className="post-box compact-post hoverable no-tap-highlight"
      onClick={() => handleOpenPost(post.id)}
    >
      <div className="post-actions-top-right">
        <span
          className="my-edit-icon"
          onClick={(e) => {
            e.stopPropagation();
            startEdit(post);
          }}
          title="Bearbeiten"
        >
          ✏️
        </span>
        <span
          className="my-delete-icon"
          onClick={(e) => {
            e.stopPropagation();
            setPostToDelete(post.id);
          }}
          title="Löschen"
        >
          🗑️
        </span>
      </div>

      {formatCreatedDate(post) && (
  <div className="forum-card-date">
    Erstellt am {formatCreatedDate(post)}
  </div>
)}

<h3>{post.title}</h3>
<p>{post.content}</p>
<span className="author-bottom-right">{authorLabel(post)}</span>
    </div>
  </div>
)
          )
        )}

        {totalPages > 1 && (
          <div className="pagination">
            <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
              ⏮
            </button>
            <button onClick={() => setCurrentPage((prev) => Math.max(prev - 10, 1))}>«</button>
            <button onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}>‹</button>
            <span>
              {currentPage} / {totalPages}
            </span>
            <button onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}>›</button>
            <button onClick={() => setCurrentPage((prev) => Math.min(prev + 10, totalPages))}>»</button>
            <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
              ⏭
            </button>
          </div>
        )}
      </div>

      {postToDelete && (
        <div className="modal-overlay" onClick={() => setPostToDelete(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Beitrag wirklich löschen?</h3>
            <p>Diese Aktion kann nicht rückgängig gemacht werden.</p>
            <div className="modal-buttons">
              <button className="delete-btn" onClick={confirmDelete}>
                Löschen
              </button>
              <button className="cancel-btn" onClick={() => setPostToDelete(null)}>
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {showImageLimitPopup && (
        <div className="modal-overlay" onClick={() => setShowImageLimitPopup(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <p>Maximal {MAX_IMAGES} Bilder erlaubt.</p>
            <button className="green-button" onClick={() => setShowImageLimitPopup(false)}>
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ForumMyPage;